import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
  Grow,
} from '@mui/material';

import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

import './SortBy.scss';

export default function SortBy({
  sortBy,
  setSortBy,
  sortByList,
  type,
  isButton,
  tooltipCopy,
  disabled,
  isLoading,
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (item, idx) => {
    setSortBy(!isButton ? item : [item, idx]);
    setOpen(false);
  };

  return (
    <span className="SortByDropDown">
      {!isButton ? (
        <>
          <span className="sort-by-label">
            {`${type}:`}
          </span>
          <IconButton
            className="sort-by-btn"
            edge="start"
            color="inherit"
            aria-label="menu"
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            disabled={disabled}
          >
            <span className="sort-by-btn-text">
              {sortBy}
              <KeyboardArrowDownOutlinedIcon />
            </span>
          </IconButton>
        </>
      ) : (
        <Tooltip
          id="sort-by-tooltip"
          title={tooltipCopy}
          placement="top"
          arrow
        >
          <span>
            <IconButton
              className={`dropdown-btn${open ? ' is-open' : ''}${isLoading ? ' loading' : ''}`}
              edge="start"
              color="inherit"
              aria-label="menu"
              ref={anchorRef}
              aria-controls={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              disabled={disabled}
            >
              <span className="sort-by-btn-text">
                {isLoading ? (
                  <>
                    <span className="dots-circle-spinner" />
                    {type}
                  </>
                ) : (
                  <>
                    {type}
                    <KeyboardArrowDownOutlinedIcon />
                  </>
                )}
              </span>
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal={false}
        modifiers={[
          {
            name: 'flip',
            enabled: true,
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'top right' }}
          >
            <Paper id="SortBy" className="sort-by-menu-paper">
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} id="menu-list-grow">
                  {sortByList.map((item, idx) => (
                    <MenuItem
                      key={`${item}+=+=${idx * Math.random(0, 100)}`}
                      className={`sort-by-item${item === sortBy ? ' active' : ''}`}
                      onClick={() => handleMenuItemClick(item, idx)}
                    >
                      <ListItemText primary={item} />
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </span>
  );
}

SortBy.propTypes = {
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  sortByList: PropTypes.array.isRequired,
  type: PropTypes.string.isRequired,
  isButton: PropTypes.bool,
  tooltipCopy: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};

SortBy.defaultProps = {
  isButton: false,
  tooltipCopy: '',
  disabled: false,
  isLoading: false,
};
